<template>
  <div>
    <b-card>
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Gestión de Catálogos de Impuestos</h5>
          <b-button variant="primary" size="sm" @click="showCreateModal">
            <b-icon icon="plus"></b-icon> Nuevo Catálogo
          </b-button>
        </div>
      </template>

      <!-- Tax Catalogs Table -->
      <b-table
        :items="taxCatalogs"
        :fields="catalogFields"
        striped hover responsive
        show-empty
        empty-text="No hay catálogos de impuestos configurados">

        <template #cell(tax_code)="row">
          <b-badge :variant="getTaxBadgeVariant(row.item.tax_code)">{{ row.item.tax_code }}</b-badge>
        </template>

        <template #cell(country_id)="row">
          {{ countryName(row.item.country_id) }}
        </template>

        <template #cell(tax_type)="row">
          {{ taxTypeLabel(row.item.tax_type) }}
        </template>

        <template #cell(tax_scope)="row">
          {{ taxScopeLabel(row.item.tax_scope) }}
        </template>

        <template #cell(is_active)="row">
          <b-badge :variant="row.item.is_active ? 'success' : 'secondary'">
            {{ row.item.is_active ? 'Activo' : 'Inactivo' }}
          </b-badge>
        </template>

        <template #cell(tax_lines_count)="row">
          <b-badge variant="info">{{ row.item.tax_lines ? row.item.tax_lines.length : 0 }} líneas</b-badge>
        </template>

        <template #cell(actions)="row">
          <b-button-group size="sm">
            <b-button variant="info" @click="editCatalog(row.item)" title="Editar">
              <b-icon icon="pencil"></b-icon>
            </b-button>
            <b-button variant="success" @click="manageTaxLines(row.item)" title="Gestionar Tasas">
              <b-icon icon="list"></b-icon>
            </b-button>
          </b-button-group>
        </template>
      </b-table>
    </b-card>

    <!-- Create/Edit Catalog Modal -->
    <b-modal
      id="modal-catalog-form"
      v-model="showCatalogModal"
      :title="catalogForm.id ? 'Editar Catálogo' : 'Crear Catálogo'"
      @hidden="resetCatalogForm"
      size="lg">

      <b-form>
        <b-row>
          <b-col md="6">
            <b-form-group label="Código de Impuesto:">
              <b-form-select v-model="catalogForm.tax_code" :options="taxCodeOptions">
                <template #first>
                  <b-form-select-option :value="null" disabled>Selecciona un código</b-form-select-option>
                </template>
              </b-form-select>
              <b-form-text>Código estándar del impuesto (IVA, IEPS, ISR, etc.)</b-form-text>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Nombre:">
              <b-form-input v-model="catalogForm.name" placeholder="Ej: IVA (El Salvador)"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Descripción:">
              <b-form-textarea v-model="catalogForm.description" rows="2" placeholder="Descripción del catálogo"></b-form-textarea>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="4">
            <b-form-group label="País:">
              <b-form-select v-model="catalogForm.country_id" :options="countryOptions"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Tipo de Cálculo:">
              <b-form-select v-model="catalogForm.tax_type" :options="taxTypeOptions"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Aplica a:">
              <b-form-select v-model="catalogForm.tax_scope" :options="taxScopeOptions"></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Vigente desde:">
              <b-form-datepicker v-model="catalogForm.effective_from"></b-form-datepicker>
            </b-form-group>
          </b-col>
          <b-col md="6" class="d-flex align-items-center">
            <b-form-checkbox v-model="catalogForm.is_active">Activo</b-form-checkbox>
          </b-col>
        </b-row>
      </b-form>

      <template #modal-footer="{ hide }">
        <b-button variant="secondary" @click="hide">Cancelar</b-button>
        <b-button variant="primary" @click="saveCatalog" :disabled="!isValidCatalog">
          {{ catalogForm.id ? 'Actualizar' : 'Crear' }}
        </b-button>
      </template>
    </b-modal>

    <!-- Tax Lines Management Modal -->
    <b-modal
      id="modal-tax-lines"
      v-model="showTaxLinesModal"
      :title="`Tasas - ${selectedCatalog.name}`"
      size="xl"
      @hidden="resetTaxLinesForm">

      <div class="mb-3">
        <h6>Tasas Existentes</h6>
        <b-table
          :items="taxLines"
          :fields="taxLineFields"
          striped hover responsive small
          show-empty
          empty-text="No hay tasas configuradas">

          <template #cell(rate_value)="row">
            {{ row.item.rate_value }}%
          </template>

          <template #cell(is_default)="row">
            <b-badge v-if="row.item.is_default" variant="primary">Predeterminada</b-badge>
          </template>

          <template #cell(is_exempt)="row">
            <b-badge v-if="row.item.is_exempt" variant="warning">Exenta</b-badge>
          </template>
        </b-table>
        <b-alert show variant="light" class="mt-2 mb-0">
          <small>Las tasas no se pueden editar ni eliminar una vez creadas. Para cambiar una tasa, crea una nueva y marca la anterior según corresponda.</small>
        </b-alert>
      </div>

      <hr>

      <div>
        <h6>Agregar Nueva Tasa</h6>
        <b-form>
          <b-row>
            <b-col md="4">
              <b-form-group label="Concepto:">
                <b-form-input v-model="taxLineForm.rate_name" placeholder="Ej: General, Reducida, Exenta"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="Valor (%):">
                <b-form-input v-model.number="taxLineForm.rate_value" type="number" step="0.01" min="0" max="100"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Vigente desde:">
                <b-form-datepicker v-model="taxLineForm.effective_from" size="sm"></b-form-datepicker>
              </b-form-group>
            </b-col>
            <b-col md="3" class="d-flex align-items-end">
              <b-form-checkbox v-model="taxLineForm.is_default" class="mr-3 mb-2">Predet.</b-form-checkbox>
              <b-form-checkbox v-model="taxLineForm.is_exempt" class="mb-2">Exenta</b-form-checkbox>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="3">
              <b-form-group label="Redondeo:">
                <b-form-select v-model="taxLineForm.rounding_method" :options="roundingOptions" size="sm"></b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Decimales:">
                <b-form-input v-model.number="taxLineForm.rounding_precision" type="number" min="0" max="6" size="sm"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6" class="d-flex align-items-end">
              <b-button variant="success" @click="saveTaxLine" :disabled="!isValidTaxLine" class="mb-3">
                <b-icon icon="plus"></b-icon> Agregar Tasa
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </div>

      <template #modal-footer="{ hide }">
        <b-button variant="secondary" @click="hide">Cerrar</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'TaxCatalogManager',
  data() {
    return {
      taxCatalogs: [],
      showCatalogModal: false,
      showTaxLinesModal: false,
      selectedCatalog: {},
      taxLines: [],

      catalogForm: this.emptyCatalog(),
      taxLineForm: this.emptyLine(),

      catalogFields: [
        { key: 'tax_code', label: 'Código', sortable: true },
        { key: 'name', label: 'Nombre', sortable: true },
        { key: 'country_id', label: 'País', sortable: true },
        { key: 'tax_type', label: 'Cálculo', sortable: true },
        { key: 'tax_scope', label: 'Aplica a', sortable: true },
        { key: 'tax_lines_count', label: 'Tasas', sortable: false },
        { key: 'is_active', label: 'Estado', sortable: true },
        { key: 'actions', label: 'Acciones' }
      ],

      taxLineFields: [
        { key: 'rate_name', label: 'Concepto', sortable: true },
        { key: 'rate_value', label: 'Tasa', sortable: true },
        { key: 'is_default', label: 'Predet.', sortable: true },
        { key: 'is_exempt', label: 'Exenta', sortable: true }
      ],

      taxCodeOptions: [
        { value: 'IVA', text: 'IVA - Impuesto al Valor Agregado' },
        { value: 'IEPS', text: 'IEPS - Impuesto Especial sobre Producción y Servicios' },
        { value: 'ISR', text: 'ISR - Impuesto Sobre la Renta' },
        { value: 'CEDULA', text: 'Cédula - Retención Cédula' },
        { value: 'ICA', text: 'ICA - Impuesto de Industria y Comercio' },
        { value: 'CUSTOM', text: 'Personalizado' }
      ],

      taxTypeOptions: [
        { value: 'percentage', text: 'Porcentaje' },
        { value: 'fixed', text: 'Importe Fijo' },
        { value: 'compound', text: 'Compuesto' }
      ],

      taxScopeOptions: [
        { value: 'both', text: 'Venta y Compra' },
        { value: 'sale', text: 'Solo Venta' },
        { value: 'purchase', text: 'Solo Compra' }
      ],

      roundingOptions: [
        { value: 'round', text: 'Normal' },
        { value: 'round_up', text: 'Hacia arriba' },
        { value: 'round_down', text: 'Hacia abajo' }
      ],

      // Seeded countries (see migration 20251024_multi_country_tax_compliance_complete)
      countryOptions: [
        { value: null, text: 'Sin país específico' },
        { value: 1, text: 'México' },
        { value: 2, text: 'Colombia' },
        { value: 3, text: 'El Salvador' },
        { value: 4, text: 'España' }
      ]
    };
  },

  computed: {
    isValidCatalog() {
      return this.catalogForm.tax_code && this.catalogForm.name && this.catalogForm.tax_type &&
        this.catalogForm.tax_scope && this.catalogForm.effective_from;
    },
    isValidTaxLine() {
      return this.taxLineForm.rate_name && this.taxLineForm.rate_value >= 0 && this.taxLineForm.effective_from;
    }
  },

  mounted() {
    this.loadTaxCatalogs();
  },

  methods: {
    emptyCatalog() {
      return {
        id: null,
        tax_code: null,
        name: '',
        description: '',
        country_id: null,
        tax_type: 'percentage',
        tax_scope: 'both',
        effective_from: moment().format('YYYY-MM-DD'),
        is_active: true
      };
    },
    emptyLine() {
      return {
        id: null,
        rate_name: '',
        rate_value: 0,
        is_default: false,
        is_exempt: false,
        rounding_method: 'round',
        rounding_precision: 2,
        effective_from: moment().format('YYYY-MM-DD')
      };
    },

    countryName(id) {
      const c = this.countryOptions.find((o) => o.value === id);
      return c ? c.text : '—';
    },
    taxTypeLabel(v) {
      const o = this.taxTypeOptions.find((x) => x.value === v);
      return o ? o.text : v;
    },
    taxScopeLabel(v) {
      const o = this.taxScopeOptions.find((x) => x.value === v);
      return o ? o.text : v;
    },

    loadTaxCatalogs() {
      let that = this;
      axios
        .get('/tax-catalogs')
        .then(function (response) {
          that.taxCatalogs = response.data || [];
        })
        .catch(function (err) {
          that.makeToast('Error', err.response?.data || 'Error al cargar catálogos', 'danger');
        });
    },

    showCreateModal() {
      this.resetCatalogForm();
      this.showCatalogModal = true;
    },

    editCatalog(catalog) {
      this.catalogForm = {
        id: catalog.id,
        tax_code: catalog.tax_code,
        name: catalog.name,
        description: catalog.description || '',
        country_id: catalog.country_id != null ? catalog.country_id : null,
        tax_type: catalog.tax_type || 'percentage',
        tax_scope: catalog.tax_scope || 'both',
        effective_from: catalog.effective_from ? moment(catalog.effective_from).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
        is_active: catalog.is_active
      };
      this.showCatalogModal = true;
    },

    saveCatalog() {
      if (!this.isValidCatalog) return;
      let that = this;
      const payload = {
        ...this.catalogForm,
        effective_from: this.catalogForm.effective_from + ' 00:00:00'
      };
      const method = this.catalogForm.id ? 'put' : 'post';
      const url = this.catalogForm.id ? `/tax-catalogs/${this.catalogForm.id}` : '/tax-catalogs';

      axios[method](url, payload)
        .then(function () {
          that.makeToast('Éxito', 'Catálogo guardado exitosamente', 'success');
          that.loadTaxCatalogs();
          that.showCatalogModal = false;
        })
        .catch(function (err) {
          that.makeToast('Error', err.response?.data || 'Error al guardar catálogo', 'danger');
        });
    },

    manageTaxLines(catalog) {
      this.selectedCatalog = catalog;
      this.loadTaxLines(catalog.id);
      this.resetTaxLineForm();
      this.showTaxLinesModal = true;
    },

    loadTaxLines(catalogId) {
      let that = this;
      axios
        .get(`/tax-catalogs/${catalogId}`)
        .then(function (response) {
          that.taxLines = response.data.tax_lines || [];
        })
        .catch(function (err) {
          that.makeToast('Error', err.response?.data || 'Error al cargar tasas', 'danger');
        });
    },

    saveTaxLine() {
      if (!this.isValidTaxLine) return;
      let that = this;
      const payload = {
        ...this.taxLineForm,
        effective_from: this.taxLineForm.effective_from + ' 00:00:00'
      };
      axios
        .post(`/tax-catalogs/${this.selectedCatalog.id}/lines`, payload)
        .then(function () {
          that.makeToast('Éxito', 'Tasa agregada exitosamente', 'success');
          that.loadTaxLines(that.selectedCatalog.id);
          that.resetTaxLineForm();
        })
        .catch(function (err) {
          that.makeToast('Error', err.response?.data || 'Error al agregar tasa', 'danger');
        });
    },

    resetCatalogForm() {
      this.catalogForm = this.emptyCatalog();
    },

    resetTaxLineForm() {
      this.taxLineForm = this.emptyLine();
    },

    resetTaxLinesForm() {
      this.taxLines = [];
      this.resetTaxLineForm();
    },

    getTaxBadgeVariant(taxCode) {
      const variants = {
        IVA: 'primary',
        IEPS: 'warning',
        ISR: 'info',
        CEDULA: 'secondary',
        ICA: 'success',
        CUSTOM: 'light'
      };
      return variants[taxCode] || 'light';
    },

    makeToast(title, message, variant) {
      this.$bvToast.toast(message, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true
      });
    }
  }
};
</script>

<style scoped>
.table-sm td, .table-sm th {
  padding: 0.3rem;
}
</style>
